import React from "react";

import { Box, Chip, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

interface EntryCardProps {
  title: string;
  body: string;
  /** Renders the "Not configured" state and points at support. */
  configured: boolean;
  badge?: string;
  onClick?: () => void;
}

/**
 * One way into a bulletin on the start screen.
 *
 * Unconfigured sources are shown rather than hidden: the design wants an agency to see what it
 * could turn on, which is why the card stays in the grid with a badge and a support pointer.
 */
export const EntryCard: React.FC<EntryCardProps> = ({
  title,
  body,
  configured,
  badge,
  onClick,
}) => {
  const { t } = useTranslation("bulletin/create-v2");

  return (
    <Paper
      variant="outlined"
      component={configured ? "button" : "div"}
      type={configured ? "button" : undefined}
      onClick={configured ? onClick : undefined}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 0.75,
        p: 2.5,
        borderRadius: 2,
        textAlign: "left",
        font: "inherit",
        width: "100%",
        cursor: configured ? "pointer" : "default",
        opacity: configured ? 1 : 0.7,
        "&:hover": configured ? { backgroundColor: "action.hover" } : undefined,
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        {/*
          `component="span"`, because `Paper` is a `<button>` when configured and MUI maps
          `subtitle1` to `<h6>` and `body2` to `<p>` — block content inside a button is invalid
          HTML. The caption below renders only in the `<div>` case, so it keeps its default.
        */}
        <Typography component="span" variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        {badge && <Chip size="small" variant="outlined" label={badge} />}
        {!configured && (
          <Chip
            size="small"
            variant="outlined"
            label={t("start.not_configured")}
          />
        )}
      </Box>

      <Typography component="span" variant="body2" color="text.secondary">
        {body}
      </Typography>

      {!configured && (
        <Typography variant="caption" color="text.secondary">
          {t("start.contact_support")}
        </Typography>
      )}
    </Paper>
  );
};
